import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  CheckoutTenderCreditCustomer,
  isCreditCustomerCheckoutTender,
} from '@open-tender/types'
import { formatDollars } from '@open-tender/utils'
import { selectCheckout, useAppSelector, selectBrand } from '@open-tender/cloud'
import { cardIconsMap } from 'components/cardIcons'
import CheckoutButton from './CheckoutButton/CheckoutButton'
import CheckoutCvvModal from './CheckoutCvvModal'

const CheckoutCreditCardsView = styled.div``

const CheckoutCreditCards = ({
  apply,
  remove,
  isPaid,
  setCvvCard,
  cvvAddedNewCard,
  resetIncorrectCvvError,
}: {
  apply: (card: any, swap?: boolean) => void
  remove: () => void
  isPaid?: boolean
  setCvvCard?: (cvv: string, data?: CheckoutTenderCreditCustomer) => void
  cvvAddedNewCard?: string // format: cvv,last4
  resetIncorrectCvvError?: () => void
}) => {
  const { check, form } = useAppSelector(selectCheckout)
  const brandConfig = useAppSelector(selectBrand)
  const require_cvv_for_saved_cards: boolean = brandConfig?.require_cvv_for_saved_cards || false
  const cards = useMemo(
    () => (check?.customer ? check.customer.credit_cards || [] : []),
    [check?.customer]
  )
  const [cardCount, setCardCount] = useState(cards.length)
  const hasCards = cards && cards.length > 0
  const appliedCard = form.tenders.find((i) =>
    isCreditCustomerCheckoutTender(i)
  ) as CheckoutTenderCreditCustomer | null
  const appliedCardId = appliedCard?.customer_card_id || null

  const formatNewCardAddedData = cvvAddedNewCard?.split(',') || []
  const newCardAdded = hasCards ? cards.find((c) => c.last4 === formatNewCardAddedData[1]) : undefined
  const cvvNewCardAdded = formatNewCardAddedData[0]

  const defaultCard = useMemo(() => require_cvv_for_saved_cards ? newCardAdded : cards.find((i) => i.is_default), [cards, require_cvv_for_saved_cards, newCardAdded])
  const defaultCardId = defaultCard ? defaultCard.customer_card_id : null
  const cardIds = cards.map((i) => i.customer_card_id)
  const missingCard = appliedCardId && !cardIds.includes(appliedCardId)

  const [showCvvModal, setShowCvvModal] = useState(false)
  const [pendingTender, setPendingTender] = useState<any>(null)

  const openCvvModal = (tender: any) => {
    setPendingTender(tender)
    setShowCvvModal(true)
  }

  const closeCvvModal = () => {
    setShowCvvModal(false)
    setPendingTender(null)
  }

  const handleCvvConfirm = async (cvv: string) => {
    if (setCvvCard) setCvvCard(cvv, pendingTender)
    closeCvvModal()
  }

  // if customer adds new default card, replace the current credit tender
  useEffect(() => {
    if (cards.length > cardCount) {
      setCardCount(cards.length)
      // auto applied for the card has just added without cvv popup!
      if (defaultCard && defaultCardId && appliedCardId !== defaultCardId) {
        const card: Omit<CheckoutTenderCreditCustomer, 'amount'> = {
          tender_type: 'CREDIT',
          cvv: cvvNewCardAdded,
          ...defaultCard,
        }
        apply(card, true)
      }
    }
  }, [
    appliedCardId,
    defaultCardId,
    defaultCard,
    apply,
    cardCount,
    cards.length,
    cvvNewCardAdded,
  ])

  useEffect(() => {
    if (missingCard) remove()
  }, [missingCard, remove])

  if (!hasCards) return null

  return (
    <CheckoutCreditCardsView>
      {cards.map((card, index) => {
        const {
          card_type_name,
          card_type,
          last4,
          customer_card_id,
          is_default,
        } = card
        const title = `${card_type_name} - ${last4}`
        const isApplied = customer_card_id === appliedCard?.customer_card_id
        const subtitle = isApplied
          ? `${formatDollars(`${appliedCard.amount ?? 0}`)} applied to check`
          : is_default
          ? 'Default Card'
          : undefined
        const disabled = isPaid && !isApplied
        const tender = { tender_type: 'CREDIT', ...card }
        const onPress = isApplied
          ? () => remove()
          : require_cvv_for_saved_cards
          ? () => {
              if (resetIncorrectCvvError) resetIncorrectCvvError()
              openCvvModal(tender)
            }
          : () => apply(tender)

        return (
          <div style={{position: 'relative'}} key={`card-${index}-${customer_card_id}`}>
            <CheckoutButton
              key={customer_card_id}
              icon={cardIconsMap[card_type ?? 'VISA']}
              title={title}
              subtitle={subtitle}
              onPress={onPress}
              isApplied={isApplied}
              disabled={disabled}
            />
          </div>
        )
      })}
      {showCvvModal && (
        <CheckoutCvvModal
          onConfirm={handleCvvConfirm}
          onClose={closeCvvModal}
        />
      )}
    </CheckoutCreditCardsView>
  )
}

export default CheckoutCreditCards
